/**
 * Notification Configuration Constants
 *
 * Centralized configuration for animations, timing, limits, and other
 * notification system constants.
 */

// Animation timing and configuration
export const notificationConfig = {
    // Bell animation timings
    bell: {
        giggleDuration: 1600, // Bell giggle animation duration (ms) – slowed for comfort
        giggleCooldown: 5000, // Minimum time between giggles (ms)
        hoverAnimationDuration: 200, // Hover animation duration (ms)
    },

    // Badge animation timings
    badge: {
        bounceDuration: 400, // Badge bounce animation duration (ms)
        countTransitionDuration: 300, // Number morphing animation duration (ms)
        maxDisplayCount: 9, // Max count before showing "9+" (number)
    },

    // Dropdown animation timings
    dropdown: {
        slideInDuration: 300, // Dropdown slide-in animation duration (ms)
        slideOutDuration: 200, // Dropdown slide-out animation duration (ms)
        itemStaggerDelay: 50, // Delay between notification item animations (ms)
        maxItems: 5, // Max items to show initially (number)
        maxHeight: '24rem', // Max dropdown height (CSS value)
        maxWidth: '28rem', // Max dropdown width (CSS value)
    },

    // Button and interaction timings
    interactions: {
        buttonPressDuration: 150, // Button press animation duration (ms)
        hoverTransitionDuration: 200, // Hover state transition duration (ms)
        focusTransitionDuration: 150, // Focus state transition duration (ms)
    },

    // Notification limits and pagination
    limits: {
        maxTitleLength: 32, // Max characters in notification title
        maxDescriptionLength: 200, // Max characters in notification description
        itemsPerPage: 20, // Items per page for pagination
        maxNotificationsInMemory: 100, // Max notifications to keep in memory
    },

    // Auto-dismissal and cleanup
    lifecycle: {
        autoMarkReadDelay: 3000, // Auto-mark as read after viewing (ms)
        oldNotificationThreshold: 7 * 24 * 3600000, // 1 week in milliseconds
    },

    // Visual styling constants
    styling: {
        borderRadius: '0.75rem', // Standard border radius for notification elements
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        backdropBlur: '8px', // Backdrop blur for glassmorphism effects
        spacing: {
            xs: '0.25rem',
            sm: '0.5rem',
            md: '1rem',
            lg: '1.5rem',
            xl: '2rem',
        },
    },

    // Responsive breakpoints
    breakpoints: {
        mobile: '768px', // Mobile breakpoint (not implemented yet)
        tablet: '1024px', // Tablet breakpoint (not implemented yet)
        desktop: '1200px', // Desktop breakpoint
    },

    // Development and testing
    development: {
        enableDebugLogs: false, // Enable console debug logs
        enableAnimationDebugging: false, // Show animation debug info
    },

    // Accessibility settings
    accessibility: {
        reducedMotionQueries: '(prefers-reduced-motion: reduce)', // CSS media query for reduced motion
        focusVisibleSelector: '.js-focus-visible', // Focus-visible selector
        announceNewNotifications: true, // Screen reader announcements for new notifications
    },

    // Error handling and retry
    errorHandling: {
        maxRetryAttempts: 3, // Max API retry attempts (future use)
        retryDelay: 1000, // Delay between retry attempts (ms)
        errorDisplayDuration: 5000, // How long to show error messages (ms)
    },
} as const

// Type-safe access to config values
export type NotificationConfig = typeof notificationConfig

// Helper functions for working with config
export const getAnimationDuration = (
    key:
        | keyof typeof notificationConfig.bell
        | keyof typeof notificationConfig.badge
        | keyof typeof notificationConfig.dropdown,
) => {
    if (key in notificationConfig.bell) return notificationConfig.bell[key as keyof typeof notificationConfig.bell]
    if (key in notificationConfig.badge) return notificationConfig.badge[key as keyof typeof notificationConfig.badge]
    if (key in notificationConfig.dropdown)
        return notificationConfig.dropdown[key as keyof typeof notificationConfig.dropdown]
    return 300 // Default fallback
}

export const shouldRespectReducedMotion = (): boolean => {
    if (typeof window === 'undefined') return false
    return window.matchMedia(notificationConfig.accessibility.reducedMotionQueries).matches
}

export const getResponsiveConfig = () => {
    if (typeof window === 'undefined') {
        return { isMobile: false, isTablet: false, isDesktop: true }
    }

    const width = window.innerWidth
    return {
        isMobile: width < parseInt(notificationConfig.breakpoints.mobile),
        isTablet:
            width >= parseInt(notificationConfig.breakpoints.mobile) &&
            width < parseInt(notificationConfig.breakpoints.tablet),
        isDesktop: width >= parseInt(notificationConfig.breakpoints.desktop),
    }
}
